import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useHealthAlertCount } from "../../../hooks/useHealthAlertCount";
import { DASHBOARD_NAV_ITEMS } from "../dashboardNavConfig";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/SearchRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import CloudUploadIcon from "@mui/icons-material/CloudUploadRounded";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNoneRounded";
import LogoutIcon from "@mui/icons-material/LogoutRounded";
import PersonIcon from "@mui/icons-material/PersonRounded";

const mobileNavLinkClass = ({ isActive }) =>
  `flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
    isActive
      ? "bg-blue-50 text-blue-700 ring-1 ring-blue-100"
      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
  }`;

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const alertCount = useHealthAlertCount();

  const displayName = user?.name || user?.email?.split("@")[0] || "User";
  const email = user?.email || "";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const closeMobileSheet = useCallback(() => setMobileSheetOpen(false), []);

  const handleLogout = () => {
    closeMobileSheet();
    setShowMenu(false);
    logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!mobileSheetOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") closeMobileSheet();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileSheetOpen, closeMobileSheet]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => {
      if (mq.matches) setMobileSheetOpen(false);
    };
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-[90] border-b border-slate-200/90 bg-white/90 backdrop-blur-md">
        {/* Mobile: logo + hamburger menu */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 md:hidden">
          <Link
            to="/dashboard"
            className="flex min-w-0 max-w-[calc(100%-3.5rem)] items-center gap-2.5 rounded-xl outline-none ring-blue-500/40 focus-visible:ring-2"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 text-white shadow-md shadow-blue-600/20">
              <AutoAwesome sx={{ fontSize: 22 }} />
            </span>
            <span className="truncate text-base font-bold tracking-tight text-slate-900">MediTrack</span>
          </Link>
          <button
            type="button"
            onClick={() => setMobileSheetOpen(true)}
            className="flex h-11 min-w-11 shrink-0 items-center justify-center rounded-xl border border-slate-200/90 bg-slate-100 text-slate-900 shadow-sm transition-colors duration-200 hover:bg-slate-200 active:scale-[0.98]"
            aria-label="Open menu"
            aria-expanded={mobileSheetOpen}
          >
            <MenuRoundedIcon sx={{ fontSize: 26 }} />
          </button>
        </div>

        {/* Desktop: full toolbar */}
        <div className="hidden min-h-16 items-center justify-between gap-4 px-6 py-4 md:flex">
          {/* SEARCH INPUT */}
          <div className="flex max-w-[480px] min-w-0 flex-1 items-center gap-2 h-10 rounded-full border border-transparent bg-gray-100 px-4 transition-all duration-200 focus-within:bg-white focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-500/20 shadow-inner">
            <SearchIcon className="shrink-0 text-gray-400" sx={{ fontSize: 20 }} />
            <input
              type="search"
              placeholder="Search reports, insights, or health data..."
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-500"
            />
          </div>

          <div className="flex shrink-0 items-center gap-3 lg:gap-4">
            {/* UPLOAD BUTTON */}
            <motion.button
              type="button"
              data-tour="navbar-upload"
              className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 px-5 text-sm font-semibold text-white shadow-md shadow-blue-500/25 transition-all duration-200 hover:brightness-105"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/upload")}
            >
              <CloudUploadIcon sx={{ fontSize: 18 }} />
              <span>Upload Report</span>
            </motion.button>

            {/* NOTIFICATION ICON */}
            <button
              type="button"
              className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-all duration-200 hover:bg-gray-200 hover:text-gray-900"
              onClick={() => navigate("/alerts")}
              aria-label={alertCount > 0 ? `Health alerts, ${alertCount} active` : "Health alerts"}
            >
              <NotificationsNoneIcon sx={{ fontSize: 22 }} />
              {alertCount > 0 ? (
                <span className="absolute right-0 top-0 flex h-[18px] min-w-[18px] items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[9px] font-bold text-white shadow-sm">
                  {alertCount > 9 ? "9+" : alertCount}
                </span>
              ) : null}
            </button>

            {/* USER PROFILE */}
            <div className="relative">
              <button
                type="button"
                className="flex items-center gap-3 rounded-full outline-none ring-blue-500/40 focus-visible:ring-2 transition-transform hover:scale-[1.02] active:scale-[0.98] py-1 pl-3 pr-1 hover:bg-gray-50 border border-transparent hover:border-gray-200"
                onClick={() => setShowMenu((v) => !v)}
                aria-expanded={showMenu}
                aria-haspopup="true"
              >
                <div className="hidden text-right lg:block">
                  <span className="block text-sm font-semibold text-gray-900 leading-tight">{displayName}</span>
                  <span className="block text-xs font-medium text-blue-600 leading-tight mt-0.5" style={{ textShadow: "0 0 1px rgba(37,99,235,0.2)"}}>Pro Member</span>
                </div>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-sm font-bold text-white shadow-sm ring-2 ring-white">
                  {initials}
                </span>
              </button>

              <AnimatePresence>
                {showMenu ? (
                  <motion.div
                    className="absolute right-0 top-full z-20 mt-2 min-w-[200px] overflow-hidden rounded-2xl border border-gray-100 bg-white py-1.5 shadow-xl ring-1 ring-black/5"
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                  >
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                      onClick={() => {
                        navigate("/settings");
                        setShowMenu(false);
                      }}
                    >
                      <PersonIcon className="text-gray-400" sx={{ fontSize: 18 }} /> 
                      <span className="font-medium">Profile Settings</span>
                    </button>
                    <hr className="my-1 border-gray-100" />
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
                      onClick={handleLogout}
                    >
                      <LogoutIcon className="text-red-500" sx={{ fontSize: 18 }} /> 
                      <span className="font-medium">Log out</span>
                    </button>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer: slides from right, backdrop closes on outside click */}
      {mobileSheetOpen ? (
        <div
          className="fixed inset-0 z-[300] flex flex-row md:hidden"
          role="presentation"
        >
          <button
            type="button"
            className="min-h-0 min-w-0 flex-1 cursor-default bg-slate-900/45 transition-opacity duration-200"
            aria-label="Close menu"
            onClick={closeMobileSheet}
          />
          <div
            className="flex h-full w-[min(100%,320px)] shrink-0 flex-col border-l border-slate-200 bg-white shadow-[-12px_0_40px_rgba(15,23,42,0.12)]"
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-4 py-3">
              <span className="text-sm font-semibold text-slate-900">Menu</span>
              <button
                type="button"
                onClick={closeMobileSheet}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                aria-label="Close menu"
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </button>
            </div>

            <div className="shrink-0 border-b border-slate-100 px-3 py-3">
              <label className="sr-only" htmlFor="mobile-nav-search">
                Search
              </label>
              <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                <SearchIcon className="shrink-0 text-slate-400" sx={{ fontSize: 20 }} />
                <input
                  id="mobile-nav-search"
                  type="search"
                  placeholder="Search reports, insights…"
                  className="min-w-0 flex-1 border-0 bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
                />
              </div>
            </div>

            <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain px-2 py-3" aria-label="App navigation">
              {DASHBOARD_NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                const badge =
                  item.badgeFromCount && alertCount > 0 ? alertCount : item.badge != null ? item.badge : null;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={mobileNavLinkClass}
                    onClick={closeMobileSheet}
                  >
                    <Icon sx={{ fontSize: 22 }} className="shrink-0 opacity-90" />
                    <span className="flex-1">{item.label}</span>
                    {badge != null && badge !== 0 ? (
                      <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white">
                        {badge > 9 ? "9+" : badge}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </nav>

            <div className="shrink-0 space-y-3 border-t border-slate-100 bg-slate-50/90 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-violet-600 text-sm font-bold text-white">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{displayName}</p>
                  {email ? <p className="truncate text-xs text-slate-500">{email}</p> : null}
                </div>
              </div>
              <button
                type="button"
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:bg-slate-50 active:scale-[0.99]"
                onClick={() => {
                  closeMobileSheet();
                  navigate("/settings");
                }}
              >
                <PersonIcon sx={{ fontSize: 20 }} />
                Account settings
              </button>
              <button
                type="button"
                className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 active:scale-[0.99]"
                onClick={handleLogout}
              >
                <LogoutIcon sx={{ fontSize: 20 }} />
                Log out
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
